import {
    AdminContentSection,
    AdminImage,
    AdminLandingPage,
    AdminLPButton,
    ApiContentBlock,
    EDIT_CHAT_MESSAGE_ROLE,
    EDIT_CHAT_SESSION_STATUS,
    EditChatAttachedImage,
    EditChatAttachedProduct,
    EditChatAttachedVideo,
    EditChatContextSelection,
    EditChatMessage,
    EditChatSession,
    EditChatSessionStatus,
    EditChatToolCallPayload,
    EditChatTurnActivity,
    EditChatTurnTimelineItem,
    DiscountType,
    FloatingButtonPatch,
    isProductTargetedButton,
    isProductTargetedButtonType,
    LP_BUTTON_TYPE,
    messagesAfterLatestUser,
    PageSettingsPatch,
    RememberedBrandGuideline,
} from '@storefronts/common-types'
import { v4 as uuid } from 'uuid'
import { SectionWithHash } from '../editorblocks/sectionWithHash'
import type { TurnChangeSummary } from './changes/types'

export type AgentChatMessage = {
    id: string
    /**
     * Stable assistant-ui message id. Survives optimistic → server id
     * promotion so the user bubble does not remount.
     */
    threadId?: string
    role: 'user' | 'assistant'
    content: string
    contextSelections?: EditChatContextSelection[]
    attachedImageUrls?: string[]
    attachedProducts?: EditChatAttachedProduct[]
    /** Display labels only — EditChatAttachedVideo on the wire is just a
     * videoHash, so a replayed/history message has no real name to show;
     * the optimistic message (built from the composer's own picked data)
     * is the only case with a real name. */
    attachedVideoNames?: string[]
    /** Durable Working-UI timeline when present on the assistant message. */
    turnActivity?: EditChatTurnActivity
    /**
     * Structured summary of everything the agent changed this turn, grouped
     * by target. Computed client-side from a pre-turn snapshot vs. the
     * post-turn editor state; not persisted across reloads. Absent on legacy
     * assistant messages that predate baseline capture.
     */
    turnChangeSummary?: TurnChangeSummary
    /** Brand guidelines the agent saved this turn; durable, from the
     * assistant message's annotation. */
    rememberedGuidelines?: RememberedBrandGuideline[]
    langsmithRunId?: string
    feedbackSubmitted?: boolean
    /** Assistant message recording a failed turn; content is the error text. */
    failed?: boolean
}

/**
 * An image attached in the composer, before it's ever sent:
 * - `library`: a media-library pick (already a permanent Cloudinary image).
 * - `uploading`: a raw file pasted/dropped in, currently uploading straight
 *   to Cloudinary from the browser (see uploadChatAttachment.ts) — the
 *   backend never sees the bytes.
 * - `uploaded`: that upload finished; `cloudinaryUrl` is real, but not yet
 *   cataloged into the media library (no `Image` DB row) unless the agent
 *   later uses it as a source.
 */
export type ComposerAttachment =
    | { kind: 'library'; image: AdminImage }
    | { kind: 'uploading'; id: string; file: File; previewUrl: string }
    | {
          kind: 'uploaded'
          id: string
          cloudinaryUrl: string
          previewUrl: string
          label: string
      }

export function getComposerAttachmentKey(
    attachment: ComposerAttachment,
): string {
    return attachment.kind === 'library'
        ? attachment.image.imageHash
        : attachment.id
}

export function getComposerAttachmentPreviewUrl(
    attachment: ComposerAttachment,
): string {
    return attachment.kind === 'library'
        ? attachment.image.imageUrl
        : attachment.previewUrl
}

export function getComposerAttachmentLabel(
    attachment: ComposerAttachment,
): string {
    if (attachment.kind === 'library') return 'Attached image'
    if (attachment.kind === 'uploading')
        return attachment.file.name || 'Pasted image'
    return attachment.label
}

export function isComposerAttachmentUploading(
    attachment: ComposerAttachment,
): boolean {
    return attachment.kind === 'uploading'
}

/** Resolves each composer attachment to the wire shape for
 * postEditChatMessage — every attachment is already a real URL by send time
 * (uploads happen as soon as a file is attached, not at send), so this is a
 * plain synchronous map. */
export function composerAttachmentsToEditChatAttachedImages(
    attachments: ComposerAttachment[],
): EditChatAttachedImage[] {
    return attachments
        .filter(
            (
                attachment,
            ): attachment is Extract<
                ComposerAttachment,
                { kind: 'library' | 'uploaded' }
            > => attachment.kind !== 'uploading',
        )
        .map((attachment) =>
            attachment.kind === 'library'
                ? {
                      kind: 'library' as const,
                      imageHash: attachment.image.imageHash,
                      imageUrl: attachment.image.imageUrl,
                  }
                : { kind: 'url' as const, sourceUrl: attachment.cloudinaryUrl },
        )
}

/**
 * A video attached in the composer via VideoAttachModal — unlike
 * ComposerAttachment there's no `uploading` variant: Mux's direct-upload
 * flow requires the modal to stay open until the upload finishes, so a
 * video only ever reaches the composer once it already has a permanent
 * videoHash (see VideoAttachModal's onVideoPicked).
 */
export type ComposerVideoAttachment = {
    videoHash: string
    name: string
    imageUrl?: string
}

/** Resolves each composer video attachment to the wire shape for
 * postEditChatMessage — EditChatAttachedVideo carries only the videoHash,
 * the agent resolves display data server-side if it needs any. */
export function composerVideoAttachmentsToEditChatAttachedVideos(
    attachments: ComposerVideoAttachment[],
): EditChatAttachedVideo[] {
    return attachments.map((attachment) => ({
        videoHash: attachment.videoHash,
    }))
}

export type AgentChatToolProgress = {
    id: string
    toolName: string
    status: EditChatToolCallPayload['status']
    content?: string
}

export type AgentChatReasoningSegment = {
    id: string
    content: string
}

export type AgentChatTimelineItem = EditChatTurnTimelineItem

export type AgentChatTurnActivity = EditChatTurnActivity

/** @deprecated Use AgentChatTimelineItem[] via getTurnTimeline instead. */
export type AgentChatTurnProgress = {
    reasoning: string | null
    tools: AgentChatToolProgress[]
}

function toolCallStatus(
    payload: unknown,
): EditChatToolCallPayload['status'] | null {
    if (!payload || typeof payload !== 'object' || !('status' in payload)) {
        return null
    }
    const status = payload.status
    if (status === 'running' || status === 'done' || status === 'error') {
        return status
    }
    return null
}

function isTurnActivityMessage(
    message: EditChatMessage,
): message is EditChatMessage & { role: 'reasoning' | 'tool' } {
    return (
        message.role === EDIT_CHAT_MESSAGE_ROLE.REASONING ||
        message.role === EDIT_CHAT_MESSAGE_ROLE.TOOL
    )
}

export function messagesToTimeline(
    messages: EditChatMessage[],
): AgentChatTimelineItem[] {
    const timeline: AgentChatTimelineItem[] = []

    for (const message of messages) {
        if (message.role === EDIT_CHAT_MESSAGE_ROLE.REASONING) {
            if (typeof message.content === 'string' && message.content.trim()) {
                timeline.push({
                    type: 'reasoning',
                    id: message.id,
                    content: message.content,
                })
            }
            continue
        }
        if (message.role !== EDIT_CHAT_MESSAGE_ROLE.TOOL || !message.toolName) {
            continue
        }
        const status = toolCallStatus(message.payload)
        if (!status) {
            continue
        }
        timeline.push({
            type: 'tool',
            id: message.id,
            toolName: message.toolName,
            status,
            content: message.content,
        })
    }

    return timeline
}

export function getTurnTimeline(
    session: EditChatSession,
): AgentChatTimelineItem[] {
    const activityMessages = messagesAfterLatestUser(session.messages).filter(
        isTurnActivityMessage,
    )
    return messagesToTimeline(activityMessages)
}

export function getLatestUserEditChatMessageId(
    messages: EditChatMessage[],
): string | null {
    for (let index = messages.length - 1; index >= 0; index -= 1) {
        const message = messages[index]
        if (message.role === EDIT_CHAT_MESSAGE_ROLE.USER) {
            return message.id
        }
    }
    return null
}

export function getLatestAgentChatUserMessageId(
    messages: AgentChatMessage[],
): string | null {
    for (let index = messages.length - 1; index >= 0; index -= 1) {
        if (messages[index].role === 'user') {
            return messages[index].id
        }
    }
    return null
}

export function getLatestAgentChatUserMessageIndex(
    messages: AgentChatMessage[],
): number {
    for (let index = messages.length - 1; index >= 0; index -= 1) {
        if (messages[index].role === 'user') {
            return index
        }
    }
    return -1
}

export function isLiveTurnTimelineCurrent(
    latestUserMessageId: string | null,
    activeTurnUserMessageId: string | null,
    previousTurnUserMessageId: string | null,
    hasPendingTurn: boolean,
): boolean {
    if (!hasPendingTurn) {
        return activeTurnUserMessageId == null
            ? true
            : latestUserMessageId === activeTurnUserMessageId
    }

    if (activeTurnUserMessageId) {
        return latestUserMessageId === activeTurnUserMessageId
    }

    if (!latestUserMessageId) {
        return false
    }

    return previousTurnUserMessageId == null
        ? true
        : latestUserMessageId !== previousTurnUserMessageId
}

export type LiveTurnHeaderStatus =
    | { kind: 'reasoning'; segmentId: string; content: string }
    | { kind: 'tool'; toolName: string }
    | null

/** Duck-typed assistant-ui parts used to drive the live Working-UI header. */
export type LiveAssistantRuntimePart = {
    type: string
    text?: string
    toolCallId?: string
    toolName?: string
    result?: unknown
    isError?: boolean
}

function toolStatusFromRuntimePart(
    part: LiveAssistantRuntimePart,
): EditChatToolCallPayload['status'] {
    if (part.result === undefined) {
        return 'running'
    }
    return part.isError ? 'error' : 'done'
}

/**
 * Live mid-turn UI comes from assistant-ui parts, not durable `turnActivity`
 * (that is stamped at turn complete). Map reasoning / tool-call parts into
 * the timeline shape `getLiveTurnHeaderStatus` already understands.
 */
export function timelineFromAssistantParts(
    parts: readonly LiveAssistantRuntimePart[],
): AgentChatTimelineItem[] {
    const timeline: AgentChatTimelineItem[] = []

    for (let index = 0; index < parts.length; index++) {
        const part = parts[index]
        if (part.type === 'reasoning') {
            const content = part.text ?? ''
            if (!content.trim()) {
                continue
            }
            timeline.push({
                type: 'reasoning',
                id: `reasoning-${index}`,
                content,
            })
            continue
        }

        if (part.type !== 'tool-call' || !part.toolName) {
            continue
        }

        timeline.push({
            type: 'tool',
            id: part.toolCallId ?? `tool-${index}`,
            toolName: part.toolName,
            status: toolStatusFromRuntimePart(part),
        })
    }

    return timeline
}

/**
 * A capture — from the capture tool or from an HTML edit — reaches the chat
 * as the tool result, collapsed by the turn stream to its URL per viewport.
 * The live AG-UI runtime hands that over already JSON-parsed; a reloaded
 * transcript hands over the raw string, and transcripts from before desktop
 * captures carry the bare mobile URL. The chat renders the pictures in place
 * of the usual tool label.
 */
const CAPTURE_TOOL_NAMES = new Set([
    'screenshot_section',
    'change_sections',
    'edit_section',
])

const CAPTURE_VIEWPORTS = ['mobile', 'desktop'] as const

export type CaptureViewport = (typeof CAPTURE_VIEWPORTS)[number]

export type SectionCapture = { viewport: CaptureViewport; url: string }

function isCaptureUrl(value: unknown): value is string {
    return typeof value === 'string' && value.startsWith('https://')
}

function captureUrlsFromResult(result: unknown): Record<string, unknown> {
    if (typeof result !== 'string') {
        return result && typeof result === 'object'
            ? (result as Record<string, unknown>)
            : {}
    }
    const text = result.trim()
    if (isCaptureUrl(text)) return { mobile: text }
    try {
        return captureUrlsFromResult(JSON.parse(text))
    } catch {
        return {}
    }
}

export function capturesFromToolPart(part: {
    toolName: string
    result?: unknown
}): SectionCapture[] {
    if (!CAPTURE_TOOL_NAMES.has(part.toolName)) return []
    const urls = captureUrlsFromResult(part.result)
    return CAPTURE_VIEWPORTS.flatMap((viewport) => {
        const url = urls[viewport]
        return isCaptureUrl(url) ? [{ viewport, url }] : []
    })
}

export function getLiveTurnHeaderStatus(
    timeline: AgentChatTimelineItem[],
): LiveTurnHeaderStatus {
    const last = getLiveTurnStatus(timeline)
    if (!last) {
        return null
    }

    if (last.type === 'tool') {
        return { kind: 'tool', toolName: last.toolName }
    }

    return {
        kind: 'reasoning',
        segmentId: last.id,
        content: last.content,
    }
}

export function getLiveTurnStatus(
    timeline: AgentChatTimelineItem[],
): AgentChatTimelineItem | null {
    if (timeline.length === 0) {
        return null
    }

    const last = timeline[timeline.length - 1]
    if (last.type === 'tool' && last.status === 'running') {
        return last
    }

    if (last.type === 'reasoning' && last.content.trim()) {
        return last
    }

    return null
}

export function getLiveTurnStartedAt(session: EditChatSession): string | null {
    const turnMessages = messagesAfterLatestUser(session.messages)
    const firstActivity = turnMessages.find(isTurnActivityMessage)
    if (firstActivity) {
        return firstActivity.createdAt
    }

    for (let index = session.messages.length - 1; index >= 0; index -= 1) {
        const message = session.messages[index]
        if (message.role === EDIT_CHAT_MESSAGE_ROLE.USER) {
            return message.createdAt
        }
    }

    return null
}

/**
 * Prefer the durable `turnActivity` stamped on the assistant message. Fall
 * back to scanning legacy reasoning/tool rows for in-TTL pre-migration sessions.
 */
export function getTurnActivityForAssistant(
    messages: EditChatMessage[],
    assistantMessageId: string,
): AgentChatTurnActivity {
    const assistantIndex = messages.findIndex(
        (message) => message.id === assistantMessageId,
    )
    if (assistantIndex < 0) {
        return { timeline: [], startedAt: null, endedAt: null }
    }

    const assistantMessage = messages[assistantIndex]
    if (assistantMessage.turnActivity) {
        return assistantMessage.turnActivity
    }

    const activityMessages: EditChatMessage[] = []
    for (let index = assistantIndex - 1; index >= 0; index -= 1) {
        const message = messages[index]
        if (message.role === EDIT_CHAT_MESSAGE_ROLE.USER) {
            break
        }
        if (isTurnActivityMessage(message)) {
            activityMessages.unshift(message)
        }
    }

    let startedAt = activityMessages[0]?.createdAt ?? null
    if (!startedAt) {
        for (let index = assistantIndex - 1; index >= 0; index -= 1) {
            const message = messages[index]
            if (message.role === EDIT_CHAT_MESSAGE_ROLE.USER) {
                startedAt = message.createdAt
                break
            }
        }
    }

    return {
        timeline: messagesToTimeline(activityMessages),
        startedAt,
        endedAt: assistantMessage.createdAt,
    }
}

const MIN_DURATION_SECONDS = 1

export function formatReasoningForDisplay(content: string): string {
    if (!content.includes('**')) {
        return content
    }

    const parts = content.split('**')
    let formatted = ''

    for (let index = 0; index < parts.length; index += 1) {
        const part = parts[index]
        const isBoldSegment = index % 2 === 1

        if (!isBoldSegment) {
            formatted += part
            continue
        }

        if (formatted.length > 0 && part.length > 0) {
            const separator = parts[index - 1] ?? ''
            const needsLineBreak =
                !formatted.endsWith('\n') &&
                (separator === '' || separator.trim() === '')

            if (needsLineBreak) {
                formatted += '\n'
            }
        }

        formatted += part
    }

    return formatted
}

export function getReasoningHeadline(content: string): string {
    const formatted = formatReasoningForDisplay(content).trim()
    if (!formatted) {
        return ''
    }

    return formatted.split(/\r?\n/)[0]?.trim() ?? ''
}

export function getReasoningBodyAfterHeadline(content: string): string {
    const formatted = formatReasoningForDisplay(content)
    const lines = formatted.split(/\r?\n/)
    if (lines.length <= 1) {
        return ''
    }

    return lines.slice(1).join('\n').trim()
}

export function formatThoughtDurationLabel(
    startedAt: string | null,
    endedAt: string | null,
): string {
    if (!startedAt || !endedAt) {
        return 'Thought for a moment'
    }

    const durationMs =
        new Date(endedAt).getTime() - new Date(startedAt).getTime()
    const durationSeconds = Math.round(durationMs / 1000)

    if (durationSeconds < MIN_DURATION_SECONDS) {
        return 'Thought for a moment'
    }

    return `Thought for ${durationSeconds}s`
}

/**
 * Reasoning + tool rows for the in-flight turn (messages after the latest user
 * message). Empty when idle / no progress has been streamed yet.
 *
 * @deprecated Prefer getTurnTimeline for chronological rendering.
 */
export function getTurnProgress(
    session: EditChatSession,
): AgentChatTurnProgress {
    const timeline = getTurnTimeline(session)
    let reasoning: string | null = null
    const tools: AgentChatToolProgress[] = []

    for (const item of timeline) {
        if (item.type === 'reasoning') {
            reasoning = item.content
            continue
        }
        tools.push({
            id: item.id,
            toolName: item.toolName,
            status: item.status,
            content: item.content,
        })
    }

    return { reasoning, tools }
}

export const OPTIMISTIC_MESSAGE_ID_PREFIX = 'optimistic-'

export function isOptimisticUserMessage(id: string): boolean {
    return id.startsWith(OPTIMISTIC_MESSAGE_ID_PREFIX)
}

export function mergeAgentChatMessagesWithOptimistic(
    current: AgentChatMessage[],
    server: AgentChatMessage[],
): AgentChatMessage[] {
    // Live turn events (RUN_STARTED, reasoning, tools) do not carry a
    // transcript. An empty snapshot must not replace durable history — that
    // unmounts the whole thread until the next MESSAGES_SNAPSHOT.
    if (server.length === 0) {
        return current
    }

    const optimisticMessages = current.filter((message) =>
        isOptimisticUserMessage(message.id),
    )
    if (optimisticMessages.length === 0) {
        return withPreservedThreadIds(current, server)
    }

    const usedOptimisticIds = new Set<string>()
    const merged = server.map((serverMessage) => {
        const currentById = current.find(
            (message) => message.id === serverMessage.id,
        )
        if (currentById) {
            return overlayCurrentMessage(serverMessage, currentById)
        }
        if (serverMessage.role !== 'user') {
            return serverMessage
        }

        const optimistic = optimisticMessages.find(
            (message) =>
                message.content === serverMessage.content &&
                !usedOptimisticIds.has(message.id),
        )
        if (!optimistic) {
            return serverMessage
        }
        usedOptimisticIds.add(optimistic.id)
        return adoptServerUserMessage(optimistic, serverMessage)
    })

    const pendingOptimistic = optimisticMessages.filter(
        (message) =>
            !usedOptimisticIds.has(message.id) &&
            !server.some((serverMessage) => serverMessage.id === message.id),
    )

    if (pendingOptimistic.length === 0) {
        return merged
    }

    if (
        current.length === merged.length + pendingOptimistic.length &&
        merged.every((message, index) => current[index] === message) &&
        pendingOptimistic.every(
            (message, index) => current[merged.length + index] === message,
        )
    ) {
        return current
    }

    return [...merged, ...pendingOptimistic]
}

function overlayCurrentMessage(
    serverMessage: AgentChatMessage,
    currentById: AgentChatMessage | undefined,
): AgentChatMessage {
    if (!currentById) {
        return serverMessage
    }
    if (currentById.role === 'user' && serverMessage.role === 'user') {
        return adoptServerUserMessage(currentById, serverMessage)
    }
    // Assistant messages: keep the client-side threadId and turnChangeSummary
    // overlays. The server has no notion of either.
    return withAssistantOverlays(serverMessage, currentById)
}

function withAssistantOverlays(
    serverMessage: AgentChatMessage,
    currentById: AgentChatMessage,
): AgentChatMessage {
    const threadId = currentById.threadId ?? serverMessage.threadId
    const turnChangeSummary =
        currentById.turnChangeSummary ?? serverMessage.turnChangeSummary
    if (
        serverMessage.threadId === threadId &&
        serverMessage.turnChangeSummary === turnChangeSummary
    ) {
        return serverMessage
    }
    return { ...serverMessage, threadId, turnChangeSummary }
}

function withPreservedThreadIds(
    current: AgentChatMessage[],
    server: AgentChatMessage[],
): AgentChatMessage[] {
    // Nothing overlay-shaped to preserve — the server snapshot wins as-is.
    if (
        !current.some(
            (message) => message.threadId || message.turnChangeSummary,
        )
    ) {
        return server
    }

    let changed = false
    const next = server.map((serverMessage) => {
        const currentById = current.find(
            (message) => message.id === serverMessage.id,
        )
        const preserved = overlayCurrentMessage(serverMessage, currentById)
        if (preserved !== serverMessage) {
            changed = true
        }
        return preserved
    })
    return changed ? next : server
}

function adoptServerUserMessage(
    current: AgentChatMessage,
    serverMessage: AgentChatMessage,
): AgentChatMessage {
    const threadId =
        current.threadId ??
        (isOptimisticUserMessage(current.id)
            ? current.id
            : serverMessage.threadId)
    const contextSelections =
        serverMessage.contextSelections ?? current.contextSelections
    const attachedImageUrls =
        serverMessage.attachedImageUrls ?? current.attachedImageUrls
    const attachedProducts =
        serverMessage.attachedProducts ?? current.attachedProducts
    const attachedVideoNames =
        serverMessage.attachedVideoNames ?? current.attachedVideoNames

    if (
        serverMessage.threadId === threadId &&
        serverMessage.contextSelections === contextSelections &&
        serverMessage.attachedImageUrls === attachedImageUrls &&
        serverMessage.attachedProducts === attachedProducts &&
        serverMessage.attachedVideoNames === attachedVideoNames
    ) {
        return serverMessage
    }

    return {
        ...serverMessage,
        threadId,
        contextSelections,
        attachedImageUrls,
        attachedProducts,
        attachedVideoNames,
    }
}

export function promoteOptimisticUserMessageId(
    messages: AgentChatMessage[],
    optimisticId: string,
    serverId: string,
): AgentChatMessage[] {
    if (optimisticId === serverId) {
        return messages
    }

    let changed = false
    const next = messages.map((message) => {
        if (message.id !== optimisticId) {
            return message
        }
        changed = true
        return {
            ...message,
            id: serverId,
            threadId: message.threadId ?? optimisticId,
        }
    })
    return changed ? next : messages
}

export function attachedImageUrlsStillDisplayed(
    messages: AgentChatMessage[],
    urls: string[],
): boolean {
    if (urls.length === 0) {
        return false
    }
    return messages.some((message) =>
        message.attachedImageUrls?.some((url) => urls.includes(url)),
    )
}

export function formatSelectionToken(
    selection: EditChatContextSelection,
): string {
    const prefix = selection.scope === 'section' ? '@section' : '@element'
    return `${prefix}[${selection.label}]`
}

export function formatAttachedProductToken(
    product: EditChatAttachedProduct,
): string {
    return `@product[${product.title}]`
}

export function formatMessageTokenBlock(
    selections: EditChatContextSelection[],
    products: EditChatAttachedProduct[],
): string {
    return [
        ...selections.map(formatSelectionToken),
        ...products.map(formatAttachedProductToken),
    ].join('\n')
}

export function createOptimisticUserMessage(
    content: string,
    attachedImageUrls: string[] = [],
    contextSelections: EditChatContextSelection[] = [],
    attachedProducts: EditChatAttachedProduct[] = [],
    attachedVideoNames: string[] = [],
): AgentChatMessage {
    const id = `${OPTIMISTIC_MESSAGE_ID_PREFIX}${uuid()}`
    return {
        id,
        threadId: id,
        role: 'user',
        content,
        contextSelections:
            contextSelections.length > 0 ? contextSelections : undefined,
        attachedImageUrls:
            attachedImageUrls.length > 0 ? attachedImageUrls : undefined,
        attachedProducts:
            attachedProducts.length > 0 ? attachedProducts : undefined,
        attachedVideoNames:
            attachedVideoNames.length > 0 ? attachedVideoNames : undefined,
    }
}

export function getUserMessageDisplayContent(
    message: AgentChatMessage,
): string {
    const tokenBlock = formatMessageTokenBlock(
        message.contextSelections ?? [],
        message.attachedProducts ?? [],
    )
    if (!tokenBlock || !message.content.startsWith(tokenBlock)) {
        return message.content
    }

    return message.content.slice(tokenBlock.length).replace(/^\n\n/, '')
}

export function pageSettingsFromLandingPage(
    landingPage: AdminLandingPage,
): PageSettingsPatch {
    const discountType =
        landingPage.discount?.type?.toUpperCase() === DiscountType.FIXED
            ? DiscountType.FIXED
            : landingPage.discount?.type
              ? DiscountType.PERCENT
              : null
    const patch: PageSettingsPatch = {
        title: landingPage.title,
        ctaPulseEnabled: landingPage.ctaPulseEnabled ?? false,
        dynamicCartUpsellsEnabled:
            landingPage.dynamicCartUpsellsEnabled ?? true,
        desktopRedirectEnabledOverride:
            landingPage.desktopRedirectEnabledOverride ?? null,
        desktopRedirectUrlOverride:
            landingPage.desktopRedirectUrlOverride ?? null,
        bannerBenefits: (landingPage.bannerBenefits ?? []).map(
            ({ id, description, isActive, type, brandBenefitId }) => ({
                ...(id != null ? { id } : {}),
                description,
                isActive,
                type,
                brandBenefitId: brandBenefitId ?? null,
            }),
        ),
        discountCode: landingPage.discountCode?.trim() || null,
        discountType,
        discountValue: landingPage.discount?.value ?? null,
    }

    if (landingPage.bannerText != null) {
        patch.bannerText = landingPage.bannerText
    }

    return patch
}

/** Undefined means the page has no floating button configured. A staged NONE
 * means the agent explicitly removed an existing one (needed on save); for
 * change detection, undefined and NONE are the same end state. */
export function floatingButtonFromLandingPage(
    landingPage: AdminLandingPage,
): FloatingButtonPatch | undefined {
    if (!landingPage.button) return undefined

    return {
        type: landingPage.button.type,
        buttonText: landingPage.button.buttonText,
        linkHash: isProductTargetedButton(landingPage.button)
            ? landingPage.button.linkHash
            : null,
        url:
            landingPage.button.type === LP_BUTTON_TYPE.LINK
                ? landingPage.button.url
                : null,
        backgroundColor: landingPage.button.backgroundColor,
        fontColor: landingPage.button.fontColor,
    }
}

function floatingButtonPatchMatches(
    button: AdminLPButton,
    patch: FloatingButtonPatch,
): boolean {
    if (button.type !== patch.type || button.buttonText !== patch.buttonText) {
        return false
    }
    if (
        button.backgroundColor !== patch.backgroundColor ||
        button.fontColor !== patch.fontColor
    ) {
        return false
    }
    if (button.type === LP_BUTTON_TYPE.LINK) {
        return button.url === (patch.url ?? '')
    }
    if (isProductTargetedButton(button)) {
        return button.linkHash === (patch.linkHash ?? '')
    }
    return true
}

export function applyFloatingButtonPatch(
    landingPage: AdminLandingPage,
    patch: FloatingButtonPatch | undefined,
): AdminLandingPage {
    if (!patch) return landingPage

    // No-op re-applies must keep the same object so deepEqual doesn't mark
    // the page dirty.
    if (
        landingPage.button &&
        floatingButtonPatchMatches(landingPage.button, patch)
    ) {
        return landingPage
    }

    // Match manual delete: a page that never had a button stays null rather
    // than materializing a NONE row that would dirty the page on save.
    if (patch.type === LP_BUTTON_TYPE.NONE && !landingPage.button) {
        return landingPage
    }

    // Match AdminLPButton shapes — leftover DB columns are cleared on save.
    const buttonHash = landingPage.button?.buttonHash ?? uuid()
    const { type, buttonText } = patch

    let button: AdminLPButton
    if (type === LP_BUTTON_TYPE.LINK) {
        button = {
            type,
            buttonHash,
            buttonText,
            url: patch.url ?? '',
        }
    } else if (isProductTargetedButtonType(type)) {
        const linkHash = patch.linkHash ?? ''
        button = {
            type,
            buttonHash,
            buttonText,
            linkHash,
        }
    } else {
        button = { type, buttonHash, buttonText }
    }

    if (patch.backgroundColor !== undefined) {
        button.backgroundColor = patch.backgroundColor
    }
    if (patch.fontColor !== undefined) {
        button.fontColor = patch.fontColor
    }

    return { ...landingPage, button }
}

function isDisplayableChatMessage(
    message: EditChatMessage,
): message is EditChatMessage & {
    role: 'user' | 'assistant'
    content: string
} {
    return (
        (message.role === EDIT_CHAT_MESSAGE_ROLE.USER ||
            message.role === EDIT_CHAT_MESSAGE_ROLE.ASSISTANT) &&
        typeof message.content === 'string' &&
        message.content.length > 0
    )
}

// Checks for 'url' specifically (rather than `=== 'library'`) so a
// pre-migration session — persisted before the `kind` discriminant existed,
// up to the store's 24h TTL — still displays correctly: those records only
// ever have `imageUrl`, never `sourceUrl`, so they fall through to the same
// branch as `library` here.
function attachedImageDisplayUrl(image: EditChatAttachedImage): string {
    return image.kind === 'url' ? image.sourceUrl : image.imageUrl
}

export function toAgentChatMessages(
    session: EditChatSession,
): AgentChatMessage[] {
    return session.messages.filter(isDisplayableChatMessage).map((message) => ({
        id: message.id,
        role: message.role,
        content: message.content,
        contextSelections: message.contextSelections,
        attachedImageUrls: message.attachedImages?.map(attachedImageDisplayUrl),
        attachedProducts: message.attachedProducts,
        // EditChatAttachedVideo only carries a videoHash — no real name to
        // show for a replayed message, unlike the optimistic message built
        // straight from the composer's own picked video data.
        attachedVideoNames: message.attachedVideos?.map((_, index, all) =>
            all.length > 1 ? `Video ${index + 1}` : 'Video',
        ),
        ...(message.role === EDIT_CHAT_MESSAGE_ROLE.ASSISTANT &&
        message.turnActivity
            ? { turnActivity: message.turnActivity }
            : {}),
    }))
}

/**
 * Prepends one `@element`/`@section`/`@product` token per attached selection
 * and product to the user's typed text. Tokens are the transcript-visible
 * summary; the structured `contextSelections`/`attachedProducts` fields carry
 * the details the backend expands into the model's prompt.
 */
export function formatTokensForContent(
    selections: EditChatContextSelection[],
    products: EditChatAttachedProduct[],
    typedContent: string,
): string {
    const tokenBlock = formatMessageTokenBlock(selections, products)
    if (!tokenBlock) return typedContent
    return `${tokenBlock}\n\n${typedContent}`
}

export function getLatestAssistantMessage(
    session: EditChatSession,
): EditChatMessage | null {
    for (let i = session.messages.length - 1; i >= 0; i -= 1) {
        const message = session.messages[i]
        if (message.role === EDIT_CHAT_MESSAGE_ROLE.ASSISTANT) {
            return message
        }
    }
    return null
}

export function shouldApplySessionToEditor(
    status: EditChatSessionStatus,
): boolean {
    return (
        status === EDIT_CHAT_SESSION_STATUS.IDLE ||
        status === EDIT_CHAT_SESSION_STATUS.AWAITING_APPROVAL
    )
}

function sectionId(section: { id?: string }): string | undefined {
    return typeof section.id === 'string' ? section.id : undefined
}

/** Maps session contentBlocks onto editor sections, reusing UI hashes when possible. */
export function sectionsWithHashesFromContentBlocks(
    contentBlocks: ApiContentBlock,
    previousSections: SectionWithHash[],
): SectionWithHash[] {
    const sections = contentBlocks.tabs?.[0]?.sections ?? []
    const previousById = new Map<string, SectionWithHash>()
    for (const previous of previousSections) {
        const id = sectionId(previous as AdminContentSection)
        if (id) {
            previousById.set(id, previous)
        }
    }

    return sections.map((section, index) => {
        const id = sectionId(section as AdminContentSection)
        const previousByStableId = id ? previousById.get(id) : undefined
        const previousByIndex = previousSections[index]
        const hash =
            previousByStableId?.hash ??
            (previousByIndex?.type === section.type
                ? previousByIndex.hash
                : undefined) ??
            uuid()

        return { ...section, hash }
    })
}

function existingDiscountType(
    landingPage: AdminLandingPage,
): DiscountType | null {
    if (!landingPage.discount) return null
    return landingPage.discount.type?.toUpperCase() === DiscountType.FIXED
        ? DiscountType.FIXED
        : DiscountType.PERCENT
}

function applyDiscountPageSettingsPatch(
    landingPage: AdminLandingPage,
    next: AdminLandingPage,
    patch: PageSettingsPatch,
): void {
    if (
        patch.discountCode === undefined &&
        patch.discountType === undefined &&
        patch.discountValue === undefined
    ) {
        return
    }

    if (patch.discountCode !== undefined) {
        next.discountCode = patch.discountCode
        if (!patch.discountCode) {
            delete next.discount
            return
        }
    }

    // Delta patches omit unchanged fields — fill from the current page so a
    // code-only or value-only change does not leave a stale discount object.
    const code = next.discountCode ?? landingPage.discountCode
    const type = patch.discountType ?? existingDiscountType(landingPage)
    const value = patch.discountValue ?? landingPage.discount?.value
    if (code && type && value != null) {
        next.discount = {
            type: type.toLowerCase(),
            value,
        }
    }
}

export function applyPageSettingsPatch(
    landingPage: AdminLandingPage,
    patch: PageSettingsPatch,
): AdminLandingPage {
    const next: AdminLandingPage = { ...landingPage }

    if (patch.title !== undefined) {
        next.title = patch.title
    }
    if (patch.bannerText !== undefined) {
        next.bannerText = patch.bannerText
    }
    if (patch.ctaPulseEnabled !== undefined) {
        next.ctaPulseEnabled = patch.ctaPulseEnabled
    }
    if (patch.dynamicCartUpsellsEnabled !== undefined) {
        next.dynamicCartUpsellsEnabled = patch.dynamicCartUpsellsEnabled
    }
    if (patch.desktopRedirectEnabledOverride !== undefined) {
        next.desktopRedirectEnabledOverride =
            patch.desktopRedirectEnabledOverride
    }
    if (patch.desktopRedirectUrlOverride !== undefined) {
        next.desktopRedirectUrlOverride = patch.desktopRedirectUrlOverride
    }
    if (patch.bannerBenefits !== undefined) {
        next.bannerBenefits = patch.bannerBenefits
    }
    applyDiscountPageSettingsPatch(landingPage, next, patch)

    return next
}

const PAGE_SETTINGS_PATCH_KEYS = [
    'bannerText',
    'ctaPulseEnabled',
    'dynamicCartUpsellsEnabled',
    'title',
    'desktopRedirectEnabledOverride',
    'desktopRedirectUrlOverride',
    'bannerBenefits',
    'discountCode',
    'discountType',
    'discountValue',
] as const satisfies ReadonlyArray<keyof PageSettingsPatch>

function pageSettingsFieldEqual(a: unknown, b: unknown): boolean {
    if (a === b) return true
    if (
        Array.isArray(a) ||
        Array.isArray(b) ||
        (a !== null && typeof a === 'object') ||
        (b !== null && typeof b === 'object')
    ) {
        return JSON.stringify(a) === JSON.stringify(b)
    }
    return false
}

/**
 * Fields the agent actually changed this turn, relative to the pageSettings
 * snapshot sent at beginTurn. Session state keeps the full begin-turn
 * snapshot when the agent touches nothing — applying that wholesale would
 * clobber concurrent editor edits (banner benefits, desktop redirect, etc.).
 */
export function pageSettingsPatchDelta(
    baseline: PageSettingsPatch,
    current: PageSettingsPatch,
): PageSettingsPatch {
    const delta: PageSettingsPatch = {}
    for (const key of PAGE_SETTINGS_PATCH_KEYS) {
        if (current[key] === undefined) continue
        if (!pageSettingsFieldEqual(baseline[key], current[key])) {
            Object.assign(delta, { [key]: current[key] })
        }
    }
    return delta
}
